import { ActionsObservable } from 'redux-observable';
import { OrderedMap, OrderedSet } from 'immutable';
import { Observable } from 'rxjs';

import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import OrganizationApi from 'entities/organization/api';
import Organization from 'entities/organization/model';
import { addMultipleAction, setDisplayedOrganizationIdsAction } from 'entities/organization/types';

import {
  FetchParams,
  fetchSuccess,
  fetchFailed,
  Init,
  initSuccess,
  SET_FETCH_PARAMS,
  INIT,
  fetchDeedsTotalSuccess,
  fetchDeedsTotalFailed,
  fetchDonationsTotalSuccess,
  fetchDonationsTotalFailed,
  fetchVolunteerTimesTotalSuccess,
  fetchVolunteerTimesTotalFailed,
  unblockNonprofitSuccessAction,
  unblockNonprofitFailedAction,
  FETCH_DONATIONS_TOTAL,
  FETCH_DEEDS_TOTAL,
  FETCH_VOLUNTEERHOURS_TOTAL,
  UNBLOCK_NONPROFIT,
} from './types';
import { selectFetchParams } from './selectors';

const fetch = (action$: ActionsObservable<Init | FetchParams>, store: any) =>
  action$.ofType(INIT, SET_FETCH_PARAMS).mergeMap(({ type }) => {
    const state = store.getState();
    const fetchParams = selectFetchParams(state);
    const sort = fetchParams.get('sort') || 'name';
    const order = fetchParams.get('order') || 'asc';
    const page = fetchParams.get('pageNum') || 0;
    const limit = fetchParams.get('limit') || 10;
    const search = fetchParams.get('search');

    return OrganizationApi.fetchNonprofits(search, null, {
      limit,
      page,
      sort,
      order,
      affiliated: true,
      includeUnapproved: true,
    })
      .mergeMap(({ results = [], total }) => [
        addMultipleAction(
          OrderedMap(
            results.map((organization: Organization) => [
              organization._id,
              new Organization(organization),
            ])
          )
        ),
        setDisplayedOrganizationIdsAction(
          OrderedSet(results.map((organization: Organization) => organization._id))
        ),
        type === INIT ? initSuccess(total) : fetchSuccess(total),
      ])
      .takeUntil(action$.ofType(INIT, SET_FETCH_PARAMS))
      .catch((e) => [fetchFailed(e)]);
  });

const fetchDonationsTotal = (action$) =>
  action$
    .ofType(FETCH_DONATIONS_TOTAL)
    .mergeMap(() =>
      OrganizationApi.fetchCompanyDonationsTotal().flatMap((totals) => [
        fetchDonationsTotalSuccess(totals?.[0]?.totalAmount || 0),
      ])
    )
    .catch((e) => [fetchDonationsTotalFailed(e)]);

const fetchDeedsTotal = (action$) =>
  action$
    .ofType(FETCH_DEEDS_TOTAL)
    .mergeMap(() =>
      OrganizationApi.fetchCompanyDeedsTotal().flatMap((totals) => [
        fetchDeedsTotalSuccess(totals[0]?.count || 0),
      ])
    )
    .catch((e) => [fetchDeedsTotalFailed(e)]);

const fetchVolunteerTimesTotal = (action$) =>
  action$
    .ofType(FETCH_VOLUNTEERHOURS_TOTAL)
    .mergeMap(() =>
      OrganizationApi.fetchCompanyVolunteerTimesTotal().flatMap((totals) => [
        fetchVolunteerTimesTotalSuccess(totals[0]?.duration || 0),
      ])
    )
    .catch((e) => [fetchVolunteerTimesTotalFailed(e)]);

const unblockNonprofit = (action$: any) =>
  action$.ofType(UNBLOCK_NONPROFIT).mergeMap(({ id }: { id: string }) =>
    OrganizationApi.unblockNonprofit(id)
      .mergeMap(() => [unblockNonprofitSuccessAction(), showMessageAction('Nonprofit unblocked!')])
      .catch((e) =>
        Observable.of(
          unblockNonprofitFailedAction(e),
          showErrorAction("An error occurred unblocking, make sure it isn't already unblocked.")
        )
      )
  );

export default [
  fetch,
  fetchDonationsTotal,
  fetchDeedsTotal,
  fetchVolunteerTimesTotal,
  unblockNonprofit,
];
